import React, { useState } from "react";
import { UtensilsCrossed, Wine, Flame } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { IMAGES, HOOKAH_FLAVORS, HOOKAH_SPECIALS, COCKTAILS, FOOD, FOOD_SIDES, FOOD_HAPPY_HOUR } from "@/lib/content";

const TABS = [
  { id: "food", label: "Food", icon: UtensilsCrossed },
  { id: "cocktails", label: "Cocktails", icon: Wine },
  { id: "hookah", label: "Hookah", icon: Flame },
];

export default function Menus() {
  const [tab, setTab] = useState("food");

  return (
    <>
      <Seo title="Menus — Food, Cocktails & Hookah" description="Explore ChiCha Lounge DC menus: a chef-curated kitchen, DC-inspired craft cocktails and 20+ premium hookah flavors on U Street." path="/menus" image={IMAGES.cocktail1} />
      <PageHero overline="Taste the District" title="Our Menus" subtitle="A chef-curated kitchen, DC-inspired craft cocktails and premium hookah." image={IMAGES.cocktail1} />

      <section className="pb-28 bg-transparent">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex flex-wrap gap-3 mb-14">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} data-testid={`menu-tab-${t.id}`} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm uppercase tracking-wide transition-all ${tab === t.id ? "bg-neutral-900 text-white" : "glass text-neutral-600"}`}>
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "food" && (
            <div className="grid lg:grid-cols-3 gap-10 items-start">
              <Stagger className="lg:col-span-2">
                <h2 className="font-serif text-4xl text-neutral-900 mb-8">The Kitchen</h2>
                <div className="space-y-5">
                  {FOOD.map((f) => (
                    <StaggerItem key={f.name}>
                      <div className="flex items-start justify-between gap-4 border-b border-black/10 pb-5">
                        <div>
                          <h3 className="text-neutral-900 font-semibold text-lg">{f.name}</h3>
                          {f.desc && <p className="text-neutral-500 text-sm mt-1 leading-relaxed">{f.desc}</p>}
                        </div>
                        <span className="font-serif text-2xl text-neutral-900 shrink-0">{f.price}</span>
                      </div>
                    </StaggerItem>
                  ))}
                </div>
              </Stagger>

              <div className="space-y-6">
                <Reveal>
                  <div className="card-lux p-8">
                    <span className="overline">Sides</span>
                    <div className="mt-5 space-y-3">
                      {FOOD_SIDES.map((s) => (
                        <div key={s.name} className="flex justify-between items-center">
                          <span className="text-neutral-600 text-sm">{s.name}</span>
                          <span className="font-serif text-xl text-neutral-900">{s.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={0.1}>
                  <div className="card-lux p-8">
                    <span className="overline">Happy Hour · 5–7PM</span>
                    <div className="mt-5 space-y-3">
                      {FOOD_HAPPY_HOUR.map((h) => (
                        <div key={h.name} className="flex justify-between items-center">
                          <span className="text-neutral-600 text-sm">{h.name}</span>
                          <span className="font-serif text-xl text-neutral-900">{h.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          )}

          {tab === "cocktails" && (
            <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {COCKTAILS.map((c) => (
                <StaggerItem key={c.name}>
                  <div className="card-lux p-6 h-full flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-serif text-2xl text-neutral-900">{c.name}</h3>
                      <span className="font-serif text-2xl text-neutral-900 shrink-0">{c.price}</span>
                    </div>
                    <p className="text-neutral-500 text-sm mt-3 leading-relaxed">{c.desc}</p>
                    {c.ingredients && (
                      <p className="text-neutral-400 text-xs mt-4 pt-4 border-t border-black/10 leading-relaxed">
                        <span className="uppercase tracking-[0.14em] text-neutral-500">Ingredients · </span>{c.ingredients}
                      </p>
                    )}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          )}

          {tab === "hookah" && (
            <div className="grid lg:grid-cols-3 gap-10 items-start">
              <Stagger className="lg:col-span-2">
                <h2 className="font-serif text-4xl text-neutral-900 mb-8">Flavors</h2>
                <div className="flex flex-wrap gap-2.5">
                  {HOOKAH_FLAVORS.map((f) => (
                    <StaggerItem key={f}><span className="border border-black/12 rounded-full px-4 py-2 text-neutral-600 hover:border-neutral-900 hover:text-neutral-900 transition-colors cursor-default">{f}</span></StaggerItem>
                  ))}
                </div>
              </Stagger>
              <Reveal>
                <div className="card-lux p-8">
                  <span className="overline">Happy Hour Specials</span>
                  <div className="mt-6 space-y-4">
                    {HOOKAH_SPECIALS.map((s) => (
                      <div key={s.label} className="flex justify-between items-center border-b border-black/10 pb-4">
                        <span className="text-neutral-600 text-sm">{s.label}</span>
                        <span className="font-serif text-2xl text-neutral-900">{s.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
